import React, {Component} from 'react';
import styled, {css} from 'styled-components';

import {buildHoverParentClassName} from '../../../../../../../libs/shared/helpers/src/browser/hoverHelpers';
import {markFullStorySensitive} from '../../../../../../helpers/fullstoryHelpers';
import {ChannelModel} from '../../../../../../models/api/channelModel';
import {
  LoadedMessageModel,
  MessageTypesEnum,
  MessageVisualStatusesEnum,
} from '../../../../../../models/api/messageModel';
import {EmlPanelId, MessagePanelId, ViewerPanelId} from '../../../../../../store/panels/panelStateBase';
import {MessageViewerActionReply} from '../action/messageViewerActionReply';
import {MessageViewerActionZone} from '../action/messageViewerActionZone';
import {MessageViewerAttachmentsContainer} from '../messageViewerAttachmentsContainer';
import {MessageViewerAvatar, MessageViewerAvatarProps} from '../messageViewerAvatar';
import {MessageViewerChatSeen} from '../messageViewerChatSeen';
import {MessageViewerDate, MessageViewerDateProps} from '../messageViewerDate';
import {MessageViewerDetails} from '../messageViewerDetails';
import {MessageViewerMoreBaseProps} from '../messageViewerMore';
import {MessageViewerMoreContainer} from '../messageViewerMoreContainer';
import {MessageViewerSeen} from '../messageViewerSeen';
import {MessageViewerVisitorUrl} from '../messageViewerVisitorUrl';
import {MessageViewerSender, MessageViewerSenderProps} from '../sender/messageViewerSender';
import {MessageViewerChatBody, MessageViewerChatBodyProps} from './messageViewerChatBody';

/*
 * Props.
 */

interface MessageViewerChatProps
  extends MessageViewerAvatarProps,
    MessageViewerChatBodyProps,
    MessageViewerMoreBaseProps,
    Omit<MessageViewerSenderProps, 'message'>,
    Omit<MessageViewerDateProps, 'message'> {
  panelId: ViewerPanelId | MessagePanelId | EmlPanelId;
  message: LoadedMessageModel;
  replyChannel?: ChannelModel;
  isChatFollowUp?: boolean;
  isInMessagePopup: boolean;
  hasNewVisitorUrlExperience?: boolean;
  hasNewChatSeenIndicator?: boolean;
  chatUserName?: string;
}

interface StyledChatDivProps {
  $isVisitorUrlIconVisible?: boolean;
}

/*
 * Style.
 */

const StyledChatDiv = styled.div<StyledChatDivProps>`
  display: grid;
  grid-template-rows: auto auto;
  align-items: center;
  padding: 8px 9px;

  ${(p) => addGridStyles(p.$isVisitorUrlIconVisible)}
`;

const StyledAvatarDiv = styled.div`
  grid-area: avatar;
  align-self: start;
`;

interface RecipientsStyleProps {
  $hasManyRecipients?: boolean;
}
const StyledRecipientsDiv = styled.div<RecipientsStyleProps>`
  grid-area: recipient;
  margin: 5px 7px;
  overflow: hidden;

  display: flex;

  ${(p) => maybeAddMarginStyles(p.$hasManyRecipients)};
`;

function maybeAddMarginStyles(hasManyRecipients?: boolean) {
  if (hasManyRecipients) {
    return css`
      margin-top: 0;
      margin-left: 0;
    `;
  }

  return '';
}

const StyledContentDiv = styled.div`
  grid-area: content;
  align-self: start;
  margin: -4px -9px -7px -9px;
  /* Allow this grid item to shrink and overflow wrap. */
  min-width: 0;
`;

const StyledFollowUpChatDiv = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: 1fr;
  grid-template-areas: 'content';
  align-items: center;
  padding: 7px 9px 7px 53px;
`;

const StyledFollowUpContentDiv = styled(StyledContentDiv)`
  margin: -7px -9px;

  /* Allow this grid item to shrink and overflow wrap. */
  min-width: 0;
`;

/* Base container for message date and more menu button. */
const StyledDetailsBaseDiv = styled.div`
  display: flex;
  align-items: center;
`;

/* Floating variation to make the message content wrap. */
const StyledDetailsSpacerDiv = styled(StyledDetailsBaseDiv)`
  float: right;
  margin-top: 6px;
  margin-right: 9px;
  visibility: hidden;
  pointer-events: none;
`;

/* Absolutely positioned variation to show over top of the message. */
const StyledDetailsDiv = styled(StyledDetailsBaseDiv)`
  position: absolute;
  top: 6px;
  right: 9px;
  transform: translateY(0.5px);
`;

const StyledMessageViewerChatBody = styled(MessageViewerChatBody)`
  padding-top: 0;
`;

/*
 * Component.
 */

export class MessageViewerChat extends Component<MessageViewerChatProps> {
  constructor(props: MessageViewerChatProps) {
    super(props);
  }

  render() {
    const messagHasBody = (this.props.message.body || '').trim().length > 0;

    return this.props.isChatFollowUp
      ? this.renderFollowUpMessage(messagHasBody)
      : this.renderFullMessage(messagHasBody);
  }

  private renderDetails() {
    const {message, visualRecipients} = this.props;
    const {to} = visualRecipients;

    // If this is SMS or if we have multiple "to" recipients, show all of them.
    const isSms = message.type === MessageTypesEnum.SMS;

    if ((to && to.length > 1) || isSms) {
      return <MessageViewerDetails {...this.props} searchQuery={this.props.cellsContext.searchQuery} />;
    }

    // Otherwise, just show the sender.
    return <MessageViewerSender {...this.props} isClickable needsParentHover />;
  }

  private renderFullMessage(messageHasBody?: boolean) {
    const {to} = this.props.visualRecipients;
    const hasManyToRecipients = to && to.length > 1;

    const layerExtra =
      this.props.message.visualStatus === MessageVisualStatusesEnum.TIMELINE && this.props.message.layerExtra;
    return (
      <>
        <StyledChatDiv
          className={buildHoverParentClassName()}
          $isVisitorUrlIconVisible={this.props.message.isInbound && this.props.hasNewVisitorUrlExperience}
        >
          <StyledAvatarDiv>
            <MessageViewerAvatar {...this.props} isChatAvatar />
          </StyledAvatarDiv>
          <StyledRecipientsDiv $hasManyRecipients={hasManyToRecipients}>
            {this.renderDetails()}
          </StyledRecipientsDiv>
          <StyledContentDiv className={markFullStorySensitive()}>
            {renderAttachments(this.props)}
            {messageHasBody && <StyledMessageViewerChatBody {...this.props} />}
          </StyledContentDiv>
          {maybeRenderSeen(this.props)}
          <MessageViewerDate message={this.props.message} isInDragRegion={this.props.isInDragRegion} />
          {this.props.message.isInbound && this.props.hasNewVisitorUrlExperience && layerExtra && (
            <MessageViewerVisitorUrl url={layerExtra.layerPageUrl} />
          )}
          <MessageViewerMoreContainer {...this.props} />
        </StyledChatDiv>
        {this.props.replyChannel && renderReplyAction(this.props)}
      </>
    );
  }

  private renderFollowUpMessage(messageHasBody?: boolean) {
    const layerExtra =
      this.props.message.visualStatus === MessageVisualStatusesEnum.TIMELINE && this.props.message.layerExtra;
    return (
      <>
        <StyledFollowUpChatDiv className={buildHoverParentClassName()}>
          <StyledFollowUpContentDiv>
            <StyledDetailsSpacerDiv>
              {maybeRenderSeen(this.props)}
              <MessageViewerDate message={this.props.message} isInDragRegion={this.props.isInDragRegion} />
              <MessageViewerMoreContainer {...this.props} />
            </StyledDetailsSpacerDiv>
            {renderAttachments(this.props)}
            {messageHasBody && <MessageViewerChatBody {...this.props} />}
          </StyledFollowUpContentDiv>
          <StyledDetailsDiv>
            {maybeRenderSeen(this.props)}
            <MessageViewerDate message={this.props.message} isInDragRegion={this.props.isInDragRegion} />
            {this.props.message.isInbound && this.props.hasNewVisitorUrlExperience && layerExtra && (
              <MessageViewerVisitorUrl url={layerExtra.layerPageUrl} />
            )}
            <MessageViewerMoreContainer {...this.props} />
          </StyledDetailsDiv>
        </StyledFollowUpChatDiv>
        {this.props.replyChannel && renderReplyAction(this.props)}
      </>
    );
  }
}

function maybeRenderSeen(props: MessageViewerChatProps) {
  if (props.message.type !== MessageTypesEnum.FRONT_CHAT) {
    return <MessageViewerSeen message={props.message} isInDragRegion={props.isInDragRegion} />;
  }

  if (!props.message.isInbound && props.hasNewChatSeenIndicator && props.message.isSeen) {
    return <MessageViewerChatSeen chatUserName={props.chatUserName} message={props.message} />;
  }

  return null;
}

function renderReplyAction(props: Readonly<MessageViewerChatProps>) {
  return (
    <MessageViewerActionZone isInPopup={props.isInMessagePopup}>
      <MessageViewerActionReply
        iconName="reply"
        onClick={props.onReplyClick}
        messageType={props.message.type}
      />
    </MessageViewerActionZone>
  );
}

function renderAttachments(props: Readonly<MessageViewerChatProps>) {
  return <MessageViewerAttachmentsContainer message={props.message} />;
}

/*
 * Helpers.
 */

function addGridStyles($isVisitorUrlIconVisible?: boolean) {
  return $isVisitorUrlIconVisible
    ? css`
        grid-template-columns: auto 1fr auto auto auto auto;
        grid-template-areas:
          'avatar recipient seen date visitor more'
          'avatar content content content content content';
      `
    : css`
        grid-template-columns: auto 1fr auto auto auto;
        grid-template-areas:
          'avatar recipient seen date more'
          'avatar content content content content';
      `;
}
